// Pricing page — public + in-app.
//
// Reads the v2 catalog (packages.legacy = false) + their package_features
// rows for credits, sites, setup fee, and a curated 6-8 feature list.
// Monthly/annual toggle: annual price = monthly × 12 × 0.83 (Save 17%),
// setup fee shown waived in the annual column.
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Loader2, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { db, money } from "@/lib/billing";
import { useAuth } from "@/hooks/useAuth";
import { useLocale, intlLocale, toLocale, dbLocaleSuffix } from "@/lib/locale";

type PackageRow = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  audience: string | null;
  monthly_price: number | null;
  price_cents: number | null;
  currency: string;
  sort_order: number;
  legacy: boolean;
  is_active: boolean;
  package_features: { feature_key: string; feature_value: unknown }[];
  setup_fees?: { amount_cents: number; currency: string }[];
};

const VISIBLE_FEATURE_KEYS = [
  "monthly_credits",
  "max_sources",
  "publish_cadence",
  "ai_images",
  "editorial_qa",
  "seo_level",
  "languages",
  "support",
] as const;

async function fetchV2Packages(): Promise<PackageRow[]> {
  const { data, error } = await db
    .from("packages")
    .select("id, slug, name, description, audience, monthly_price, price_cents, currency, sort_order, legacy, is_active, package_features (feature_key, feature_value), setup_fees (amount_cents, currency)")
    .eq("legacy", false)
    .eq("is_active", true)
    .order("sort_order");
  if (error) throw error;
  return (data as PackageRow[]) ?? [];
}

function featureValueByKey(pkg: PackageRow, key: string): unknown {
  return pkg.package_features.find((pf) => pf.feature_key === key)?.feature_value;
}

function formatFeatureValue(key: string, value: unknown, t: (k: string, opts?: Record<string, unknown>) => string): string {
  if (value === null || value === undefined) return t("packages.unlimited");
  if (key === "monthly_credits" && typeof value === "object") {
    const fv = value as { amount?: number };
    return t("pricing.creditsPerMonth", { n: fv.amount ?? 0 });
  }
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "✓" : "—";
  return JSON.stringify(value);
}

export default function BillingPlansPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = intlLocale(toLocale(i18n.language));
  const [annual, setAnnual] = useState(false);

  const { data: packages = [], isLoading, error } = useQuery({ queryKey: ["v2-packages"], queryFn: fetchV2Packages });

  // Split into visible tiles + enterprise CTA.
  const { visible, enterprise } = useMemo(() => {
    const v: PackageRow[] = [];
    let e: PackageRow | null = null;
    for (const pkg of packages) {
      if (pkg.slug.startsWith("enterprise")) e = pkg;
      else if (pkg.slug.startsWith("trial")) continue;  // trial is sign-up flow only
      else v.push(pkg);
    }
    return { visible: v, enterprise: e };
  }, [packages]);

  function priceFor(pkg: PackageRow): { display: string; monthlyEquivalent: number | null } {
    const cents = pkg.price_cents ?? 0;
    if (cents === 0) return { display: t("packages.custom"), monthlyEquivalent: null };
    if (!annual) {
      return { display: money(cents / 100, pkg.currency, false, locale), monthlyEquivalent: cents };
    }
    // Annual: 12 × monthly × 0.83 ≈ 17% saving.
    const annualTotal = Math.round(cents * 12 * 0.83);
    const monthlyEquivalent = Math.round(annualTotal / 12);
    return { display: money(monthlyEquivalent / 100, pkg.currency, false, locale), monthlyEquivalent };
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="container mx-auto px-6 py-6">
          <h1 className="text-3xl font-bold">{t("pricing.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("pricing.subtitle")}</p>
          <div className="flex items-center justify-center gap-3 mt-6">
            <Label htmlFor="annual-toggle" className={!annual ? "font-medium" : "text-muted-foreground"}>
              {t("pricing.billingMonthly")}
            </Label>
            <Switch id="annual-toggle" checked={annual} onCheckedChange={setAnnual} />
            <Label htmlFor="annual-toggle" className={annual ? "font-medium" : "text-muted-foreground"}>
              {t("pricing.billingAnnual")}
              <Badge variant="default" className="ml-2 text-[10px]">{t("pricing.annualSavings")}</Badge>
            </Label>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8">
        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <Card><CardContent className="p-6 text-sm text-destructive">{t("billing.loadError")}</CardContent></Card>
        ) : (
          <>
            <div className="grid gap-5 grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
              {visible.map((pkg) => {
                const { display } = priceFor(pkg);
                const setupCents = pkg.setup_fees?.[0]?.amount_cents ?? null;
                const slugSimple = pkg.slug.replace(/-v2$/, "");
                const isPopular = slugSimple === "growth";
                return (
                  <Card key={pkg.id} id={slugSimple} className={isPopular ? "border-primary ring-2 ring-primary/20" : undefined}>
                    <CardHeader className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <CardTitle className="text-lg">{pkg.name}</CardTitle>
                          <CardDescription className="text-xs mt-1 min-h-10">{pkg.audience}</CardDescription>
                        </div>
                        {isPopular && <Badge>{t("pricing.popularBadge")}</Badge>}
                      </div>
                      <div>
                        <div className="text-3xl font-bold">{display}</div>
                        <p className="text-xs text-muted-foreground">
                          {annual ? t("pricing.perMonthAnnual") : t("pricing.perMonth")}
                        </p>
                        {setupCents !== null && setupCents > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {annual ? (
                              <Badge variant="secondary" className="text-[10px]">{t("pricing.setupFeeWaived")}</Badge>
                            ) : (
                              t("pricing.setupFee", { amount: money(setupCents, pkg.currency, true) })
                            )}
                          </p>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-2">
                          {t("pricing.featuresTitle")}
                        </div>
                        <ul className="space-y-2">
                          {VISIBLE_FEATURE_KEYS.map((key) => {
                            const value = featureValueByKey(pkg, key);
                            if (value === undefined || value === false) return null;
                            return (
                              <li key={key} className="flex items-start gap-2 text-sm">
                                <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <span>
                                  <span className="font-medium">{t(`pricing.feat.${key}`, { defaultValue: key })}: </span>
                                  <span className="text-muted-foreground">{formatFeatureValue(key, value, t)}</span>
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>

                      <Button asChild className="w-full" disabled={!user}>
                        <Link to={`/checkout/package/${pkg.id}${annual ? "?billing=annual" : ""}`}>
                          <Sparkles className="mr-2 h-4 w-4" />
                          {t("pricing.ctaSelect", { plan: pkg.name })}
                        </Link>
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Enterprise CTA — separate, full-width below */}
            {enterprise && (
              <Card id="enterprise" className="mt-6 border-dashed">
                <CardContent className="p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-semibold">{t("pricing.enterpriseTitle")}</h3>
                    <p className="text-sm text-muted-foreground">{t("pricing.enterpriseSubtitle")}</p>
                  </div>
                  <Button asChild variant="outline">
                    <Link to="/contact?subject=enterprise">{t("pricing.ctaEnterprise")}</Link>
                  </Button>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
}
